import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { riderStatusApi } from '../api/riderStatusApi';
import type {
    ContractStatusCounts,
    Rider,
    RiderApiItem,
    RiderFilter,
    RiderFilterState,
    RiderSearchResponseItem,
    Supplier,
} from '../types/riderStatus.types';
import { ITEMS_PER_PAGE, STORAGE_KEYS } from '../constants/riderStatus.constants';
import { getItem } from '@/shared/utils/storage';
import { ContractBadge, PrepaymentBadge } from '@/shared/components/dataDisplay/Badge';
import { loadFilters } from '@/shared/utils/filterStorage';

export const useRiderData = (
    filters: RiderFilterState,
    setFilters: React.Dispatch<React.SetStateAction<RiderFilterState>>,
) => {
    const navigate = useNavigate();
    const userType = getItem<string>('USER_TYPE');
    const userId = getItem<string>('USER_ID');

    const [allRiders, setAllRiders] = useState<Rider[]>([]);
    const [riders, setRiders] = useState<Rider[]>([]);
    const [rawRiderData, setRawRiderData] = useState<RiderApiItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = ITEMS_PER_PAGE.DEFAULT;

    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [suppliersLoading, setSuppliersLoading] = useState<boolean>(
        userType === 'HEADQUARTERS',
    );
    const [defaultSupplierSet, setDefaultSupplierSet] = useState(false);

    const updatePageData = useCallback(
        (data: Rider[], page: number) => {
            const start = (page - 1) * itemsPerPage;
            setRiders(data.slice(start, start + itemsPerPage));
            setTotalPages(Math.ceil(data.length / itemsPerPage));
            setCurrentPage(page);
        },
        [itemsPerPage],
    );

    const handlePageChange = useCallback(
        (page: number) => {
            updatePageData(allRiders, page);
        },
        [allRiders, updatePageData],
    );

    const handleRiderClick = useCallback(
        (riderId: string) => {
            navigate('/dashboard', { state: { page: 'rider_detail', riderId } });
        },
        [navigate],
    );

    const mapRiderData = useCallback(
        (riderList: RiderApiItem[]): Rider[] =>
            riderList.map((item) => {
                let supplierName = item.centerName || item.suppName;
                if (!supplierName && item.suppId && suppliers.length > 0) {
                    supplierName = suppliers.find((s) => s.centerId === item.suppId)?.name;
                }
                return {
                    id: item.riderId || '',
                    name: item.name || '-',
                    phoneNumber: item.phoneNumber || '-',
                    joinDate: item.insDate || '-',
                    contractStatusName:
                        item.status?.contractStatusName ||
                        item.status?.description ||
                        item.contractStatusName ||
                        item.contStatus,
                    deliveryMethName: item.deliveryMethName,
                    insurCodeName: item.insurCodeName,
                    insurStatusCodeName: item.insurStatusCodeName,
                    preAcctYn: item.preAcctYn,
                    supplierName: supplierName || '-',
                    suppId: item.suppId,
                    contractEndDate: item.contEdDate || '-',
                    riderType: item.riderType,
                    balAmt: item.balAmt,
                    registNum: item.registNum,
                    vpayRegistYn: item.vpayRegistYn,
                };
            }),
        [suppliers],
    );

    const buildCleanFilters = (raw: RiderFilterState, applyUserId: boolean): RiderFilter => {
        const base = applyUserId && userType === 'SUPPLIER' && userId
            ? { ...raw, centerId: userId }
            : { ...raw };

        const clean: RiderFilter = {};
        if (base.centerId) clean.centerId = base.centerId;
        if (base.riderName) clean.riderName = base.riderName;
        if (base.riderId) clean.riderId = base.riderId;
        if (base.phoneNum) clean.phoneNum = base.phoneNum;
        if (base.contStatus) clean.contStatus = base.contStatus;
        return clean;
    };

    const fetchRiders = useCallback(
        async (customFilters?: RiderFilter): Promise<void> => {
            try {
                setLoading(true);
                const searchFilters = customFilters || buildCleanFilters(filters, true);
                const shouldUseSearch =
                    userType === 'SUPPLIER' ||
                    Object.values(searchFilters).some((v) => v !== '' && v !== undefined);

                const response = shouldUseSearch
                    ? await riderStatusApi.searchRiders(searchFilters)
                    : await riderStatusApi.getRiderList(searchFilters);

                const riderList: RiderApiItem[] = Array.isArray(response) ? response : [];
                setRawRiderData(riderList);
                const mapped = mapRiderData(riderList);
                setAllRiders(mapped);
                updatePageData(mapped, 1);
            } catch {
                setAllRiders([]);
                setRiders([]);
                setRawRiderData([]);
                setTotalPages(1);
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userType, userId, mapRiderData, updatePageData, filters],
    );

    const searchRiders = useCallback(async (): Promise<void> => {
        setCurrentPage(1);
        await fetchRiders(buildCleanFilters(filters, true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchRiders, filters]);

    const fetchSuppliers = useCallback(async (): Promise<void> => {
        if (userType !== 'HEADQUARTERS') return;
        try {
            setSuppliersLoading(true);
            const response = await riderStatusApi.getSuppliers();

            let raw: RiderSearchResponseItem[] = [];
            if (Array.isArray(response)) raw = response;
            else if ('data' in response && Array.isArray(response.data)) raw = response.data;
            else if ('suppliers' in response && Array.isArray(response.suppliers))
                raw = response.suppliers;

            setSuppliers(
                raw.map((item) => ({
                    centerId: item.centerId ?? item.id ?? item.center_id ?? '',
                    name: item.name ?? item.centerName ?? '',
                    statusCode:
                        typeof item.statusCode === 'string' ? item.statusCode : item.status?.code,
                    statusDesc: item.statusDesc ?? item.status?.desc ?? item.statusDescription,
                })),
            );
        } catch {
            setSuppliers([]);
        } finally {
            setSuppliersLoading(false);
        }
    }, [userType]);

    const getContractBadge = useCallback(
        (status: string): React.ReactElement =>
            React.createElement(ContractBadge, { status }),
        [],
    );

    const getPrepaymentBadge = useCallback(
        (prepayment: boolean): React.ReactElement =>
            React.createElement(PrepaymentBadge, { prepayment }),
        [],
    );

    // 초기 데이터 로드
    useEffect(() => {
        const initialFetch = async () => {
            const savedFilters = loadFilters<RiderFilter>(STORAGE_KEYS.RIDER_STATUS_FILTERS);

            if (userType === 'HEADQUARTERS' && !savedFilters?.centerId) {
                setLoading(true);
                return;
            }

            try {
                setLoading(true);
                const base: RiderFilter = savedFilters || {};
                if (userType === 'SUPPLIER' && userId) base.centerId = userId;

                const shouldUseSearch = !!savedFilters || userType === 'SUPPLIER';
                const clean: RiderFilter = {};
                if (base.centerId) clean.centerId = base.centerId;
                if (base.riderName) clean.riderName = base.riderName;
                if (base.riderId) clean.riderId = base.riderId;
                if (base.phoneNum) clean.phoneNum = base.phoneNum;
                if (base.contStatus) clean.contStatus = base.contStatus;

                const response = shouldUseSearch
                    ? await riderStatusApi.searchRiders(clean)
                    : await riderStatusApi.getRiderList(clean);

                const riderList: RiderApiItem[] = Array.isArray(response) ? response : [];
                setRawRiderData(riderList);
                const mapped = mapRiderData(riderList);
                setAllRiders(mapped);
                updatePageData(mapped, 1);
            } catch {
                setAllRiders([]);
                setRiders([]);
                setRawRiderData([]);
                setTotalPages(1);
            } finally {
                setLoading(false);
            }
        };

        initialFetch();
        fetchSuppliers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType, userId]);

    // suppliers 로드 완료 후 협력사명 재매핑
    useEffect(() => {
        if (suppliers.length > 0 && rawRiderData.length > 0) {
            const mapped = mapRiderData(rawRiderData);
            setAllRiders(mapped);
            updatePageData(mapped, currentPage);
        }
    }, [suppliers, rawRiderData, mapRiderData, currentPage, updatePageData]);

    // 본사 사용자: 협력사 로드 후 기본 협력사 자동 선택
    useEffect(() => {
        if (userType !== 'HEADQUARTERS' || defaultSupplierSet || suppliersLoading) return;

        const savedFilters = loadFilters<RiderFilter>(STORAGE_KEYS.RIDER_STATUS_FILTERS);
        if (savedFilters?.centerId || filters.centerId) {
            setDefaultSupplierSet(true);
            return;
        }

        setDefaultSupplierSet(true);
        if (suppliers.length > 0 && suppliers[0]?.centerId) {
            const newFilters = { ...filters, centerId: suppliers[0].centerId };
            setFilters(newFilters);
            fetchRiders(buildCleanFilters(newFilters, false));
        } else {
            fetchRiders(buildCleanFilters(filters, false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType, suppliers, suppliersLoading, defaultSupplierSet]);

    const contractStatusCounts = useMemo((): ContractStatusCounts => {
        const counts = allRiders.reduce(
            (acc, rider) => {
                const s = rider.contractStatusName;
                if (s === '계약중' || s === 'UNDER_CONTRACT') acc.underContract++;
                else if (s === '계약종료' || s === 'TERMINATED_CONTRACT') acc.terminatedContract++;
                else if (s === '계약대기' || s === 'CONTRACT_PENDING') acc.contractPending++;
                return acc;
            },
            { underContract: 0, terminatedContract: 0, contractPending: 0 },
        );
        return { total: allRiders.length, ...counts };
    }, [allRiders]);

    return {
        riders,
        loading,
        currentPage,
        totalPages,
        suppliers,
        suppliersLoading,
        contractStatusCounts,
        userType,
        handlePageChange,
        handleRiderClick,
        searchRiders,
        getContractBadge,
        getPrepaymentBadge,
    };
};
